/**
 * OrbField — el fondo que hace visible el vidrio.
 *
 * Portado de `clobi-design-system@0.1.0`. Va **una vez por shell**, detrás de
 * todo el contenido.
 *
 * ─── Por qué existe ───────────────────────────────────────────────────────────
 *
 * 🔴 Sin él, las superficies de vidrio del sistema son indistinguibles del
 * fondo. Medido en la app antes de traerlo: el canvas es UN color plano, así
 * que `backdrop-filter: blur()` no tiene nada que difuminar y el 58% de blanco
 * sobre `slate-50` da `rgb(252,253,253)` contra un fondo `rgb(248,250,252)` —
 * cuatro puntos de diferencia. Las cuatro superficies se veían iguales entre sí.
 *
 * Los colores salen de la MARCA (`--rgb-orb-*` apuntan a los verdes de Clobi),
 * no del sistema, que usa su azul y su violeta.
 *
 * ⚠️ `intensity` por defecto es 0.9 y es deliberado: el fondo se percibe entre
 * los paneles, no compite con los datos. Subir de 1 es para login o marketing.
 */
import type { CSSProperties } from 'react'

interface Orbe {
  cls: string
  token: string
  alpha: number
  size: string
  left?: string
  right?: string
  top?: string
  bottom?: string
  blur: number
}

/** Posiciones y tamaños del sistema, sin cambios: son su composición. */
const ORBES: Orbe[] = [
  { cls: 'cl-orb-a', token: '--rgb-orb-1', alpha: 1.15, size: '70vw', left: '-14vw', top: '-22vh', blur: 90 },
  { cls: 'cl-orb-b', token: '--rgb-orb-2', alpha: 0.85, size: '58vw', right: '-12vw', top: '8vh', blur: 96 },
  { cls: 'cl-orb-c', token: '--rgb-orb-3', alpha: 0.75, size: '52vw', left: '22vw', bottom: '-26vh', blur: 100 },
  { cls: 'cl-orb-d', token: '--rgb-orb-4', alpha: 0.55, size: '42vw', right: '18vw', bottom: '-14vh', blur: 110 },
]

export interface OrbFieldProps {
  /** Multiplica el alfa base del tema. `0` lo apaga; `>1` lo vuelve protagonista. */
  intensity?: number
  /** La retícula de puntos sobre los orbes. */
  grid?: boolean
  className?: string
  style?: CSSProperties
}

export function OrbField({ intensity = 0.9, grid = true, className = '', style }: OrbFieldProps) {
  return (
    // aria-hidden: es decoración. Un lector de pantalla no tiene nada que decir
    // de cuatro manchas de color, y pointer-events lo saca del camino del clic.
    <div aria-hidden="true" className={`cl-orbs ${className}`.trim()} style={style}>
      {ORBES.map(o => (
        <div
          key={o.cls}
          className={`cl-orb ${o.cls}`}
          style={{
            width: o.size,
            height: o.size,
            left: o.left,
            right: o.right,
            top: o.top,
            bottom: o.bottom,
            filter: `blur(${o.blur}px)`,
            background: `radial-gradient(circle at 50% 50%, rgb(var(${o.token})) 0%, transparent 62%)`,
            opacity: `calc(var(--alpha-orb) * ${o.alpha * intensity})`,
          }}
        />
      ))}
      {grid && <div className="cl-orb-grid" />}
    </div>
  )
}
